import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Header } from "./components/Header";
import { ZoneHeroCards } from "./components/ZoneHeroCards";
import { ZoneDetailHeader } from "./components/ZoneDetailHeader";
import { OutletCard } from "./components/OutletCard";
import { ForaLogo, ArenaCateringLogo } from "./components/ForaLogo";
import { translations } from "./data/translations";
import { Loader2, RefreshCw } from "lucide-react";
import AdminPage from "./pages/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [language, setLanguage] = useState("en");
  const [selectedZone, setSelectedZone] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [zoneCounts, setZoneCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = translations[language];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [outletsRes, zonesRes] = await Promise.all([
        axios.get(`${API}/outlets`),
        axios.get(`${API}/zones`),
      ]);
      setOutlets(outletsRes.data.outlets);
      setZoneCounts(zonesRes.data.zones);
    } catch (err) {
      console.error("Failed to fetch outlets:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectZone = (zone) => {
    setSelectedZone(zone);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToZones = () => {
    setSelectedZone(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filteredOutlets = selectedZone
    ? outlets.filter((o) => o.zone === selectedZone)
    : [];

  if (window.location.pathname.startsWith("/admin")) {
    return <AdminPage />;
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#f9f9f9] shadow-2xl relative flex flex-col">
      <Header language={language} setLanguage={setLanguage} />

      {/* Loading state */}
      {loading && (
        <div
          data-testid="loading-state"
          className="flex-1 flex flex-col items-center justify-center py-20 gap-3"
        >
          <Loader2 className="animate-spin text-[#dd260e]" size={32} />
          <p className="text-sm text-gray-500">{t.loading}</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div
          data-testid="error-state"
          className="flex-1 flex flex-col items-center justify-center py-20 gap-3"
        >
          <p className="text-sm text-gray-500">{t.error}</p>
          <button
            data-testid="retry-btn"
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-[#dd260e] text-white rounded-full text-sm font-semibold"
          >
            <RefreshCw size={14} />
            {t.retry}
          </button>
        </div>
      )}

      {/* Main content */}
      {!loading && !error && (
        <>
          {!selectedZone ? (
            /* ========== ZONE SELECTION VIEW ========== */
            <div className="flex-1">
              {/* Venue header */}
              <div className="px-4 pt-5 pb-1">
                <p className="text-xs font-bold uppercase tracking-widest text-[#dd260e]">
                  {t.venue_subtitle}
                </p>
                <h1
                  data-testid="venue-title"
                  className="text-2xl sm:text-3xl font-bold tracking-tight text-[#000000] mt-1"
                >
                  {t.venue_title}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  3 {language === 'pl' ? 'strefy' : 'zones'}
                </p>
              </div>

              {/* Zone hero cards */}
              <ZoneHeroCards
                zoneCounts={zoneCounts}
                language={language}
                onSelectZone={handleSelectZone}
                t={t}
              />
            </div>
          ) : (
            /* ========== ZONE DETAIL VIEW ========== */
            <div className="flex-1">
              <ZoneDetailHeader
                zone={selectedZone}
                count={filteredOutlets.length}
                language={language}
                t={t}
                onBack={handleBackToZones}
              />

              {/* Outlet list */}
              <div className="px-4 py-3 space-y-2">
                {filteredOutlets.length === 0 ? (
                  <p data-testid="zone-empty-state" className="text-sm text-gray-400 text-center py-10">
                    {language === "pl"
                      ? "Obecnie brak czynnych punktów w tej strefie"
                      : "No points currently open in this zone"}
                  </p>
                ) : (
                  filteredOutlets.map((outlet, idx) => (
                    <OutletCard
                      key={outlet.id}
                      outlet={outlet}
                      language={language}
                      t={t}
                      index={idx}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <footer
        data-testid="app-footer"
        className="border-t border-[#e8e8e8] bg-white px-4 py-4 flex items-center justify-center gap-3 mt-auto"
      >
        <span className="text-xs text-gray-400">{t.powered_by}</span>
        <ForaLogo height="h-4" />
        <div className="w-px h-3 bg-[#e8e8e8]" />
        <ArenaCateringLogo height="h-4" />
      </footer>
    </div>
  );
}

export default App;
